import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Typography,
  TableMetadata,
  useTable,
  Label,
  Button,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as Download } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { getStatusColor } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";

import {
  subsequentSearchRequests,
  getSubsequentRequestListExportFile as getSubsequentRequestListExportFileApi,
  getZipDocumentByRequestId,
} from "../../../api";
import {
  subsequentRequestBasicInfoUrl,
  requestDraftDetailUrl,
  useTypedSelector,
} from "../../../app";
import {
  AdministrationList,
  AdministrationListFilters,
  AdministrationListHeader,
} from "../../../components";
import { useBusinessRule, usePermission } from "../../../hooks";
import {
  EnrollmentTypeEnum,
  PermissionEnum,
  RequestFilters,
  RequestSearchItem,
} from "../../../models";
import { updateSubsequentRequestFilters } from "../../../slices";
import { CreateRequestModal } from "../RegularEnrollmentRequestList/CreateRequestModal";
import { RequestSearchFilters } from "../RegularEnrollmentRequestList/RequestSearchFilters";
import styles from "./SubsequentEnrollmentRequestList.module.scss";

export const SubsequentEnrollmentRequestList = () => {
  const { hasPermission } = usePermission();
  const { t } = useTranslation();
  const history = useHistory(); const dispatch = useDispatch();

  const requestFilters = useTypedSelector(
    (state) => state.requestFilter.subsequentRequestFilters
  );

  const [isFirstRender, setIsFirstRender] = useState(true);

  const [requests, setRequests] = useState<PagedResult<RequestSearchItem>>(({
    rowCount: 0,
    results: [],
  } as unknown) as PagedResult<RequestSearchItem>);
  const [showCreateRequestModal, setShowCreateRequestModal] = React.useState(
    false
  );

  const { canCreateRequest, canDownloadExcelRequests } = useBusinessRule();

  const filtersRef = useRef<RequestFilters>(
    {} as RequestFilters
  );

  const handleDetailsClicked = (id?: number, guid?: string) => {
    if (id) {
      history.push(subsequentRequestBasicInfoUrl(id!));
    } else if (guid) {
      const request = requests.results.find((r) => r.guid === guid);
      if (request) {
        window.location.href = requestDraftDetailUrl(
          guid!,
          request.applicantOib
        );
      }
    }
  };

  const handleDownloadExcel = () => {
    getSubsequentRequestListExportFileApi(tableState);
  };

  const handleDownlodPdf = (id: number) => {
    getZipDocumentByRequestId(id);
  };
  const requestTableMetadata = useMemo(
    () =>
      ({
        rowKey: "id",
        rowToolTip: () => {},
        columns: [
          {
            columnKey: "childColumn",
            header: t("requestList.child"),
            accessor: (request) => {
              return (
                <>
                  <Typography variant="baseBold">
                    {`${request.childFirstName} ${request.childLastName}`}
                  </Typography>
                  <Typography
                    variant="base"
                    color="light"
                  >{`OIB: ${request.childOib}`}</Typography>
                </>
              );
            },
          },
          {
            columnKey: "applicantColumn",
            header: t("requestList.requestApplicant"),
            accessor: (request) => {
              return (
                <>
                  <Typography variant="baseBold">
                    {`${request.applicantFirstName} ${request.applicantLastName}`}
                  </Typography>
                  <Typography
                    variant="base"
                    color="light"
                  >{`OIB: ${request.applicantOib}`}</Typography>
                </>
              );
            },
          },
          {
            columnKey: "createdByAdminColumn",
            header: t("requestList.createdBy"),
            accessor: (request) => {
              return (
                <>
                  <Typography variant="baseBold">{``}</Typography>
                  <Typography variant="base" color="light">
                    {request.createdByAdmin
                      ? `${t("common.kindergaten")}`
                      : `${t("common.websystem")}`}
                  </Typography>
                </>
              );
            },
          },
          {
            columnKey: "statusColumn",
            header: t("requestList.requestStatus"),
            accessor: (request) => {
              return (
                <div className={styles.requestStatus}>
                  <Label color={getStatusColor(request.requestStatusId)}>
                    {request.requestStatusName}
                  </Label>
                </div>
              );
            },
          },
        ],
      } as TableMetadata<RequestSearchItem>),
    [t]
  );

  const [tableProps, tableState, setTableFilters] = useTable<RequestFilters>({
    metadata: requestTableMetadata,
    data: requests.results,
    pagination: true,
    totalItems: requests.rowCount,
  });

  const handleFiltersChanged = useCallback(
    (filters: RequestFilters) => {
      if (isFirstRender) {
        setIsFirstRender(false);
        if (requestFilters) {
          setTableFilters(requestFilters);
        }
        else {
          setTableFilters(filters);
        }
      }
      else {
        setTableFilters(filters);  
        dispatch(updateSubsequentRequestFilters(filters));
      }
      filtersRef.current = filters;
        
    },[setTableFilters, isFirstRender]
  );

  useEffect(() => {
    (async () => {
      if (tableState.pedagogicalYearId) {
        const res = await subsequentSearchRequests(tableState);
        setRequests(res);
      }
    })();
  }, [tableState]);

  const handleOpenCreateRequestModal = () => {
    setShowCreateRequestModal(true);
  };

  const handleCloseCreateRequestModal = () => {
    setShowCreateRequestModal(false);
  };

  return (
    <>
      {hasPermission(PermissionEnum.RequestRead) && (
        <>
          <AdministrationListHeader
            infoMessage={t("requestList.mainInfoMessage")}
            title={t("requestList.subsequentHeader")}
          />

          <AdministrationListFilters
            isFilteredByYear
            isFilteredByMonth
            callbackOnSearchClick
            onFiltersChanged={handleFiltersChanged}
            initialFilterValue={requestFilters}
          >
            <RequestSearchFilters />
          </AdministrationListFilters>

          <AdministrationList
            isCreateEnabled={canCreateRequest(
              requestFilters && requestFilters.pedagogicalYearId ? requestFilters.pedagogicalYearId : filtersRef.current.pedagogicalYearId || 0,
              EnrollmentTypeEnum.SubsequentEnrollment
            )}
            onAddNewClicked={handleOpenCreateRequestModal}
            tableProps={tableProps}
            isDetailsEnabled
            isDownloadEnabled
            onDetailsClicked={handleDetailsClicked}
            onDownloadPdfClicked={handleDownlodPdf}
          />
          <Row className={`${styles.buttonsLayout}`}>
            <Col
              md={12}
              className={`${styles.withoutPadding} ${styles.buttonsRight}`}
             >
                <Button
                  size="sm"
                  color="primary"
                  disabled={ !requests || requests.results.length === 0}
                  outline
                  icon={<Download />}
                  onClick={handleDownloadExcel}
                  className={`${styles.buttonSpace}`}
                >
                  {t("request.initialConversation.download")}
                </Button>
             
            </Col>
          </Row>
          <CreateRequestModal
            showCreateRequestModal={showCreateRequestModal}
            closeModal={handleCloseCreateRequestModal}
          />
        </>
      )}
    </>
  );
};
